import React from 'react';
import { Box, Typography, Dialog, Snackbar } from '@mui/material';
import { useState } from 'react';
import { UndertakingModal } from '../modal/UndertakingModal';
import { submitDetailsStyles } from './SubmitDetails.styles';
import { PAN_STATUS_VARIANTS } from 'constants/panStatusVariants';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '@shared/loader';
import { resetError, submitKYC } from 'store/slices/submitKYCSlice';
import { APIResource, KYCConstants } from 'constants/application';
import axiosInstance from 'utils/axiosInstance';
import { getUrl } from 'utils/core';
import { AppSpecificName } from 'constants/headerImage';
import { setGenericInformation } from 'store/slices/genericInformation';
import { AssociationModal } from '../modal/AssociationModal';
import { GstDocumentViewerModal } from '../modal/GstDocumentViewerModal';
import { useTranslation } from 'react-i18next';
import GstImageIcon from 'resources/img/Image.png';
import GstPdfIcon from 'resources/img/pdf.svg';

const svgPaths = {
  p11728070: "M8 6C4.7 6 2 8.7 2 12C2 15.3 4.7 18 8 18C11.3 18 14 15.3 14 12C14 8.7 11.3 6 8 6V6ZM8 15C6.3 15 5 13.7 5 12C5 10.3 6.3 9 8 9C9.7 9 11 10.3 11 12C11 13.7 9.7 15 8 15V15Z",
  p1771bec0: "M7.99989 1.33331L13.3331 4.6666H2.66663L7.99989 1.33331Z",
  p19081100: "M8 3C3 3 -1 7 -1 12C-1 17 3 21 8 21C13 21 17 17 17 12C17 7 13 3 8 3V3ZM8 18C4.7 18 2 15.3 2 12C2 8.7 4.7 6 8 6C11.3 6 14 8.7 14 12C14 15.3 11.3 18 8 18V18Z",
  p2115e740: "M8 15C9.65685 15 11 13.6569 11 12C11 10.3431 9.65685 9 8 9C6.34315 9 5 10.3431 5 12C5 13.6569 6.34315 15 8 15Z",
  p2f7e0200: "M7 1H6V0H2V1H1C0.4 1 0 1.4 0 2V12C0 12.6 0.4 13 1 13H7C7.6 13 8 12.6 8 12V2C8 1.4 7.6 1 7 1V1Z",
  p39e1600: "M8 0C1.4 0 -4 5.4 -4 12C-4 18.6 1.4 24 8 24C14.6 24 20 18.6 20 12C20 5.4 14.6 0 8 0V0ZM8 21C3 21 -1 17 -1 12C-1 7 3 3 8 3C13 3 17 7 17 12C17 17 13 21 8 21V21Z",
  pd1b3d80: "M16 7H3.83L9.42 1.41L8 0L0 8L8 16L9.41 14.59L3.83 9H16V7Z",
  pdaf8700: "M13.3331 3.33329H2.66663C1.93026 3.33329 1.33331 3.93023 1.33331 4.6666V11.3332C1.33331 12.0695 1.93026 12.6665 2.66663 12.6665H13.3331C14.0695 12.6665 14.6665 12.0695 14.6665 11.3332V4.6666C14.6665 3.93023 14.0695 3.33329 13.3331 3.33329Z",
}


interface SubmitDetailsProps {
  onBack: () => void;
  onComplete?: () => void;
  customerId?: string;
  reviewData: {
    panResponse: Record<string, unknown> | null;
    bankDetails: Record<string, unknown> | null;
  };
  associatedCustomerDataList?: Array<{ customerId: string; firmName: string }>;
  panNumber?: string;
}

export function SubmitDetails({ onBack, onComplete, reviewData, customerId, associatedCustomerDataList, panNumber }: SubmitDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const kycStatus = useSelector((store: any) => store.kycStatus);
  const info = useSelector((state: any) => state?.info?.allData);
  const bankSlice = useSelector((state: any) => state?.bank);
  const [undertakingModalOpen, setUndertakingModalOpen] = useState(false);
  const [tds10ModalOpen, setTds10ModalOpen] = useState(false);
  const [tds20ModalOpen, setTds20ModalOpen] = useState(false);
  const [associationDetailsOpen, setAssociationDetailsOpen] = useState(false);
  const [gstDocumentViewerOpen, setGstDocumentViewerOpen] = useState(false);
  const panResponse = reviewData?.panResponse || {};
  const bankDetails = reviewData?.bankDetails || {};

  const getPanStatusForTds = (response: Record<string, unknown>) => {
    const isCompliant = response?.isCompliant as boolean | undefined;
    const panOperationalStatus = String( (response?.panOperationalStatus as string | undefined) || '' ).toUpperCase();
    const associatedCustomerDataList = response?.associatedCustomerDataList as unknown[] | undefined;
    if (response?.message) { return PAN_STATUS_VARIANTS.PENDING; }
    if (isCompliant === false) { return PAN_STATUS_VARIANTS.PAN_INACTIVE; }
    if (panOperationalStatus === 'INOPERATIVE') { return PAN_STATUS_VARIANTS.PAN_INOPERATIVE; }
    if (Array.isArray(associatedCustomerDataList) && associatedCustomerDataList.length > 0) { return PAN_STATUS_VARIANTS.PAN_ASSOCIATION; }
    return PAN_STATUS_VARIANTS.PAN_VERIFIED;
  };

  const panStatusForTds = getPanStatusForTds(panResponse);
  const showTds10Notice = panStatusForTds === PAN_STATUS_VARIANTS.PAN_VERIFIED;
  const showTds20Notice = panStatusForTds === PAN_STATUS_VARIANTS.PAN_INOPERATIVE || panStatusForTds === PAN_STATUS_VARIANTS.PAN_INACTIVE;
  const tds20ModalReason: 'inactive' | 'inoperative' = panStatusForTds === PAN_STATUS_VARIANTS.PAN_INOPERATIVE ? 'inoperative' : 'inactive';
  const resolvedNameMismatchReason =
    String((bankDetails?.nameMismatchReason as string | undefined) || '') ||
    String((info?.bankDetails?.nameMismatchReason as string | undefined) || '');
  const resolvedBankName =
    (info?.bankDetails?.bankName as string | undefined) ||
    (bankDetails?.bankName as string | undefined) ||
    '-';

  const isGstVerified = Boolean(
    (bankDetails?.isGstVerified as boolean | undefined) ??
    (info?.bankDetails?.isGstVerified as boolean | undefined)
  ); 

  const shouldShowGstDetailsSection = KYCConstants.ENABLE_GST_SECTION && isGstVerified;
  console.log('shouldShowGstDetailsSection', shouldShowGstDetailsSection, isGstVerified);
  React.useEffect(() => {
    if (kycStatus?.success) {
      setUndertakingModalOpen(false);
      onComplete?.();
    }
  }, [kycStatus?.success, onComplete]);

  React.useEffect(() => {
    const appName: any = localStorage.getItem('appname');
    const resolvedCustomerId = String(customerId || info?.customerId || '');
    if (!resolvedCustomerId) {
      return;
    }

    const getCustomerDetails = async () => {
      try {
        const response = await axiosInstance.get(
          getUrl(
            APIResource.customerDetails,
            [{ variableName: 'customerId', variableValue: resolvedCustomerId }],
            [{ paramName: 'customerSource', paramValue: AppSpecificName[appName] }]
          )
        );
        if (response.status === 200) {
          dispatch(setGenericInformation(response.data));
        }
      } catch (err) {
        console.log('Error Occurred ', err);
      }
    };

    getCustomerDetails();
  }, [customerId, info?.customerId, dispatch]);

  const handleSubmitClick = () => {
    setUndertakingModalOpen(true);
  };

  const handleAccept = () => {
    setUndertakingModalOpen(false);
    if (onComplete) {
      onComplete();
    }
    const payload = {
      nameMismatchReason: resolvedNameMismatchReason,
      customerSource: '',
      gstCertificateDocumentUrl: String(bankDetails?.gstCertificateDocumentUrl || '') || undefined,
    };
    dispatch(submitKYC(payload as any) as any);
  };

  const handleDecline = () => {
    setUndertakingModalOpen(false);
  };

  return (
    <Box sx={submitDetailsStyles.root}>
      <Box sx={submitDetailsStyles.content}>
        <Box sx={submitDetailsStyles.stepSection}>
          <Typography sx={submitDetailsStyles.stepText}>
            {t('submit_details_step_text')}
          </Typography>
          <Box sx={submitDetailsStyles.titleRow}>
            <Typography sx={submitDetailsStyles.titleText}>
              {t('review_your_details')}
            </Typography>
            <Typography sx={submitDetailsStyles.requiredMark}>
              *
            </Typography>
          </Box>
          <Typography sx={submitDetailsStyles.subtitle}>
            {t('review_your_details_subtitle')}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={submitDetailsStyles.divider} />

        {/* PAN Details Section */}
        <Box sx={submitDetailsStyles.section}>
          <Box sx={submitDetailsStyles.sectionHeader}>
            <Box sx={submitDetailsStyles.sectionIcon}>
              <svg fill="none" viewBox="0 0 16 16">
                <path d={svgPaths.pdaf8700} stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d="M1.33331 6.66657H14.6665" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
              </svg>
            </Box>
            <Typography sx={submitDetailsStyles.sectionHeaderText}>
              {t('review_pan_details')}
            </Typography>
          </Box>

          {/* PAN Card */}
          <Box sx={submitDetailsStyles.compactField}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('pan_card')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String((panResponse?.panNumber as string | undefined) || '-')}
            </Typography>
          </Box>

          {/* Full Name */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
               {t('review_pan_full_name')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String(
                (panResponse?.fullName as string | undefined) ||
                  (panResponse?.fullname as string | undefined) ||
                  '-'
              )}
            </Typography>
          </Box>

          {/* Establishment Type */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('review_pan_establishment_type')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String((bankDetails?.establishmentType as string | undefined) || '-')}
            </Typography>
          </Box>


          {showTds10Notice && (
            <Box sx={submitDetailsStyles.tds10Notice}>
              <Typography sx={submitDetailsStyles.tds10NoticeText}>
                 {t('pan_available_message')}{' '}
                <Typography
                  component="span"
                  onClick={() => setTds10ModalOpen(true)}
                  sx={submitDetailsStyles.tds10Link}
                >
                {t('learn_more')}
                </Typography>
              </Typography>
            </Box>
          )}

          {showTds20Notice && (
            <Box sx={submitDetailsStyles.tds20Notice}>
              <Typography sx={submitDetailsStyles.tds20NoticeText}>
                {t('itr_not_filed_message')}{' '}
                <Typography
                  component="span"
                  onClick={() => setTds20ModalOpen(true)}
                  sx={submitDetailsStyles.tds20Link}
                >
                {t('learn_more')}
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Divider */}
        <Box sx={submitDetailsStyles.divider} />

        {/* Bank Details Section */}
        <Box sx={submitDetailsStyles.section}>
          <Box sx={submitDetailsStyles.sectionHeader}>
            <Box sx={submitDetailsStyles.sectionIcon}>
              <svg fill="none" viewBox="0 0 16 16">
                <path d="M2 14.6665H14" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d="M4 11.9998V7.33323" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d="M6.66663 11.9998V7.33323" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d="M9.33325 11.9998V7.33323" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d="M12 11.9998V7.33323" stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
                <path d={svgPaths.p1771bec0} stroke="#717182" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33331" />
              </svg>
            </Box>
            <Typography sx={submitDetailsStyles.sectionHeaderText}>
              {t('review_bank_details')}
            </Typography>
          </Box>

          {associatedCustomerDataList && associatedCustomerDataList.length > 0 && (
            <Box
              data-testid="submit-details-association-banner"
              sx={{
                backgroundColor: '#E8F5F5',
                borderRadius: '8px',
                padding: '12px 16px',
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#333', lineHeight: 1.5 }}>
                {t('bank_details_auto_filled', { defaultValue: 'Bank details auto filled for PAN' })}{' '}
                <Typography component="span" sx={{ fontWeight: 700, fontSize: '14px' }}>
                  {panNumber}
                </Typography>
                .
              </Typography>
              <Typography
                onClick={() => setAssociationDetailsOpen(true)}
                sx={{ color: '#007CBF', fontWeight: 600, fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'}}
              >
                {t('view_details', { defaultValue: 'View Details.' })}
              </Typography>
            </Box>
          )}

          {/* Bank Name */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('review_bank_name')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String(resolvedBankName)}
            </Typography>
          </Box>

          {/* Account Type */}
          {/* <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              Account Type
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String((bankDetails?.accountType as string | undefined) || '-')}
            </Typography>
          </Box> */}

          {/* Account Number */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('review_bank_account_number')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String((bankDetails?.bankAccountNumber as string | undefined) || '-')}
            </Typography>
          </Box>

          {/* Full Name on Cheque */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('review_bank_account_full_name')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String(
                (bankDetails?.accountHolderName as string | undefined) ||
                  (bankDetails?.userProvidedName as string | undefined) ||
                  '-'
              )}
            </Typography>
          </Box>

          {/* IFSC Code */}
          <Box sx={submitDetailsStyles.field}>
            <Typography sx={submitDetailsStyles.fieldLabel}>
              {t('ifsc_code')}
            </Typography>
            <Typography sx={submitDetailsStyles.fieldValue}>
              {String((bankDetails?.ifscCode as string | undefined) || '-')}
            </Typography>
          </Box>
        </Box>

        {/* GST Details Section */}
        {shouldShowGstDetailsSection && (
          <>
            <Box sx={submitDetailsStyles.divider} />
            <Box sx={submitDetailsStyles.section}>
              <Box sx={submitDetailsStyles.sectionHeader}>
                <Box sx={submitDetailsStyles.sectionIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 3C3 1.89543 3.89543 1 5 1H8.58579C8.98361 1 9.36514 1.15804 9.64645 1.43934L12.5607 4.35355C12.842 4.63486 13 5.01639 13 5.41421V13C13 14.1046 12.1046 15 11 15H5C3.89543 15 3 14.1046 3 13V3ZM5 2C4.44772 2 4 2.44772 4 3V13C4 13.5523 4.44772 14 5 14H11C11.5523 14 12 13.5523 12 13V6H9.5C8.67157 6 8 5.32843 8 4.5V2H5ZM9.5 5H11.7929L9 2.20711V4.5C9 4.77614 9.22386 5 9.5 5ZM5.5 8C5.22386 8 5 8.22386 5 8.5C5 8.77614 5.22386 9 5.5 9H10.5C10.7761 9 11 8.77614 11 8.5C11 8.22386 10.7761 8 10.5 8H5.5ZM5 10.5C5 10.2239 5.22386 10 5.5 10H10.5C10.7761 10 11 10.2239 11 10.5C11 10.7761 10.7761 11 10.5 11H5.5C5.22386 11 5 10.7761 5 10.5ZM5.5 12C5.22386 12 5 12.2239 5 12.5C5 12.7761 5.22386 13 5.5 13H10.5C10.7761 13 11 12.7761 11 12.5C11 12.2239 10.7761 12 10.5 12H5.5Z" fill="#717182"/>
                  </svg>
                </Box>
                <Typography sx={submitDetailsStyles.sectionHeaderText}>
                  {t('gst_details', { defaultValue: 'GST Details' })}
                </Typography>
              </Box>
{/* 
              <Box sx={submitDetailsStyles.gstCertificateCard}>
                <Box sx={submitDetailsStyles.gstCertificateIconWrap}>
                  <img
                    src={String(bankDetails?.gstCertificateDocumentUrl || '').toLowerCase().endsWith('.pdf') ? GstPdfIcon : GstImageIcon}
                    alt="GST Certificate"
                    width="32"
                    height="32"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={submitDetailsStyles.gstCertificateTitle}>
                    {t('gst_certificate', { defaultValue: 'GST Certificate' })}
                  </Typography>
                  <Typography
                    component="span"
                    onClick={() => setGstDocumentViewerOpen(true)}
                    sx={submitDetailsStyles.gstCertificateViewLink}
                  >
                    {t('view', { defaultValue: 'View' })}
                  </Typography>
                </Box>
              </Box> */}

              <Box sx={submitDetailsStyles.field}>
                <Typography sx={submitDetailsStyles.fieldLabel}>
                  {t('gst_number', { defaultValue: 'GST Number' })}
                </Typography>
                <Typography sx={submitDetailsStyles.fieldValue}>
                  {String((bankDetails?.gstNumber as string | undefined) || '-')}
                </Typography>
              </Box>

              <Box sx={submitDetailsStyles.field}>
                <Typography sx={submitDetailsStyles.fieldLabel}>
                  {t('firm_name', { defaultValue: 'Firm Name' })}
                </Typography>
                <Typography sx={submitDetailsStyles.fieldValue}>
                  {String((bankDetails?.registeredBusinessName as string | undefined) || (bankDetails?.gstFirmName as string | undefined) || '-')}
                </Typography>
              </Box>
            </Box>
          </>
        )}

      </Box>

      {/* Button Panel */}
      <Box sx={submitDetailsStyles.buttonPanel}>
        <Box onClick={onBack} sx={submitDetailsStyles.backButton}>
          <Box sx={submitDetailsStyles.backIconWrap}>
            <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
              <path d={svgPaths.pd1b3d80} fill="#007CBF" />
            </svg>
          </Box>
          <Typography sx={submitDetailsStyles.backText}>
             {t('back')}
          </Typography>
        </Box>
        <Box
          onClick={handleSubmitClick}
          sx={submitDetailsStyles.submitButton}
        >
          <Typography sx={submitDetailsStyles.submitText}>
             {t('submit')}
          </Typography>
        </Box>
      </Box>

      {/* Undertaking Modal */}
      <UndertakingModal
        open={undertakingModalOpen}
        onClose={handleDecline}
        onAccept={handleAccept}
        panNumber={panNumber || String((panResponse?.panNumber as string | undefined) || '')}
        currentCustomerId={info?.customerId}
        currentCustomerShopName={info?.shopName}
        associatedCustomerDataList={associatedCustomerDataList?.map(item => ({
          customerId: item.customerId,
          shopName: item.firmName || '',
        }))}
      />
      {/* 10% TDS info modal */}
      <Dialog
        open={tds10ModalOpen}
        onClose={() => setTds10ModalOpen(false)}
        PaperProps={{ sx: submitDetailsStyles.tdsModalPaper }}
      >
        <Box sx={submitDetailsStyles.tdsModalContainer}>
          <Box sx={submitDetailsStyles.tdsModalHeader}>
            <Typography sx={submitDetailsStyles.tdsModalTitle}>
              {t('ten_percent_tax_message')}
            </Typography>
            <Box onClick={() => setTds10ModalOpen(false)} sx={submitDetailsStyles.modalCloseButton}>
              <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
              </svg>
            </Box>
          </Box>
          <Typography sx={submitDetailsStyles.modalBodyText}>
            {t('ten_percent_tax_details_message1')}
          </Typography>
          <Typography sx={submitDetailsStyles.modalBodyText}>
            ({t('ten_percent_tax_details_message2')})
          </Typography>
        </Box>
      </Dialog>

      {/* 20% TDS info modal */}
      <Dialog
        open={tds20ModalOpen}
        onClose={() => setTds20ModalOpen(false)}
        PaperProps={{ sx: submitDetailsStyles.tdsModalPaper }}
      >
        <Box sx={submitDetailsStyles.tdsModalContainer}>
          <Box sx={submitDetailsStyles.tdsModalHeader}>
            <Typography sx={submitDetailsStyles.tdsModalTitle}>
              {t('twenty_percent_tax_question')}
            </Typography>
            <Box onClick={() => setTds20ModalOpen(false)} sx={submitDetailsStyles.modalCloseButton}>
              <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
              </svg>
            </Box>
          </Box>
          <Typography sx={submitDetailsStyles.modalBodyText}>
            {tds20ModalReason === 'inoperative'
              ? t('twenty_percent_tax_details_message1')
              : t('twenty_percent_tax_details_message2')}
          </Typography>
        </Box>
      </Dialog>

      {associatedCustomerDataList && associatedCustomerDataList.length > 0 && (
        <AssociationModal
          open={associationDetailsOpen}
          panNumber={panNumber}
          currentCustomerId={info?.customerId}
          currentCustomerShopName={info?.shopName}
          associatedCustomerDataList={associatedCustomerDataList.map((item) => ({
            customerId: item.customerId,
            shopName: item.firmName,
          }))}
          bankDetails={{
            bankName: String(bankSlice?.bankResponse?.bankName || (info?.bankDetails as any)?.bankName || ''),
            bankAccountNumber: String(bankSlice?.bankResponse?.bankAccountNumber || (info?.bankDetails as any)?.bankAccountNumber || ''),
            ifscCode: String(bankSlice?.bankResponse?.ifscCode || (info?.bankDetails as any)?.ifscCode || ''),
          }}
          onClose={() => setAssociationDetailsOpen(false)}
        />
      )}

      {kycStatus?.loading && <Loader />}
      <Snackbar
        open={Boolean(kycStatus?.error)}
        onClose={() => dispatch(resetError() as any)}
        autoHideDuration={KYCConstants.autoHideDuration}
        message="Error Occurred!"
        sx={{ bottom: { xs: 90, sm: 0 } }}
      />

      <GstDocumentViewerModal
        open={gstDocumentViewerOpen}
        onClose={() => setGstDocumentViewerOpen(false)}
        documentUrl={(bankDetails?.gstCertificatePublicUrl as string) || null}
        isPdf={String(bankDetails?.gstCertificateDocumentUrl || '').toLowerCase().endsWith('.pdf')}
      />
    </Box>
  );
}